import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import {
  ResetPasswordForm,
  ForgotPasswordForm,
  UserManagement,
  SpaceManagement,
  ProjectManagement,
  Dashboard,
  TaskDashboard,
  Timesheet,
  BacklogManagement,
  LoginBackground,
  SprintTimeline,
  SprintManagement,
  DailyStandup
} from '../view';
import Settings from '../view/settings/Settings';
// AI Developer Agent settings route - commented for future implementation.
// import AIDeveloperSettings from '../view/settings/ai-developer-setting/AIDeveloperSettings';
import PromptTemplates from '../view/settings/prompt-templates-setting/PromptTemplates';
// AI Agent Model settings route - commented for future implementation.
// import AIAgentModelSettings from '../view/settings/ai-agent-model-setting/AIAgentModelSettings';
import ProjectAlerts from '../view/settings/project-alerts-setting/ProjectAlerts';
import NotificationSettings from '../view/settings/notification-settings/NotificationSettings';
import Notifications from '../view/notifications/Notifications';
import StrategistAgentDashboard from '../view/strategist-agent/StrategistAgentDashboard';
import SprintSettings from '../view/settings/sprint-settings/SprintSettings';
import StrategistModelSettings from '../view/settings/strategist-model-setting/StrategistModelSettings';

import TaskViewPage from '../view/dashboard/TaskViewPage';
import ProtectedRoute from './ProtectedRoute';

const AppRoutes = () => {
  const location = useLocation();
  
  // Update document title based on route
  useEffect(() => {
    const titles = {
      '/login': 'Orkaflo Login - PMPI',
      '/forgot-password': 'Forgot Password - PMPI',
      '/reset-password': 'Reset Password - PMPI',
      '/dashboard': 'Dashboard - PMPI',
      '/backlog-management': 'Backlog Management - PMPI',
      '/sprint-timeline': 'Sprint Timeline - PMPI',
      '/ticket-management': 'Ticket Management - PMPI',
      '/user-management': 'User Management - PMPI',
      '/space-management': 'Space Management - PMPI',
      '/project-management': 'Project Management - PMPI',
      '/timesheet': 'Timesheet - PMPI',
      '/settings': 'Settings - PMPI',
      // AI Developer Agent settings route - commented for future implementation.
      // '/ai-developer-settings': 'AI Developer Settings - PMPI',
      '/prompt-templates': 'Prompt Templates - PMPI',
      // AI Agent Model settings route - commented for future implementation.
      // '/ai-agent-model-settings': 'AI Agent Model Settings - PMPI',
      '/project-alerts': 'Project Alerts - PMPI',
      '/notification-settings': 'Notification Settings - PMPI',
      '/notifications': 'Notifications - PMPI',
      '/strategist-agent': 'Strategist Agent - PMPI'
    };
    
    if (location.pathname.startsWith('/task/')) {
      document.title = 'Task Details - PMPI';
    } else {
      document.title = titles[location.pathname] || 'PMPI';
    }
  }, [location]);
  
  // Prevent router navigation if MSAL auth is in progress
  useEffect(() => {
    const hasAuthParams = location.hash.includes('access_token') || 
                         location.hash.includes('id_token') || 
                         location.search.includes('code=') ||
                         location.search.includes('error=');
    
    if (hasAuthParams && location.pathname === '/') {
      // Don't navigate away from root when auth params are present
      return;
    }
  }, [location]);
  
  return (
    <Routes>
      <Route path="/login" element={<LoginBackground />} />
      <Route path="/forgot-password" element={<ForgotPasswordForm />} />
      <Route path="/reset-password" element={<ResetPasswordForm />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/backlog-management" 
        element={
          <ProtectedRoute>
            <BacklogManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/sprint-timeline" 
        element={
          <ProtectedRoute>
            <SprintTimeline />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/sprint-management" 
        element={
          <ProtectedRoute>
            <SprintManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/daily-standup" 
        element={
          <ProtectedRoute>
            <DailyStandup />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/ticket-management" 
        element={
          <ProtectedRoute>
            <TaskDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/task/:ticket_number/*"
        element={
          <ProtectedRoute>
            <TaskViewPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/user-management" 
        element={
          <ProtectedRoute>
            <UserManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/space-management" 
        element={
          <ProtectedRoute>
            <SpaceManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/project-management" 
        element={
          <ProtectedRoute>
            <ProjectManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/timesheet" 
        element={
          <ProtectedRoute>
            <Timesheet />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } 
      />
      {/* AI Developer Agent settings route - commented for future implementation. */}
      {/* <Route 
        path="/ai-developer-settings" 
        element={
          <ProtectedRoute>
            <AIDeveloperSettings />
          </ProtectedRoute>
        } 
      /> */}
      <Route 
        path="/prompt-templates" 
        element={
          <ProtectedRoute>
            <PromptTemplates />
          </ProtectedRoute>
        } 
      />
      {/* AI Agent Model settings route - commented for future implementation. */}
      {/* <Route 
        path="/ai-agent-model-settings" 
        element={
          <ProtectedRoute>
            <AIAgentModelSettings />
          </ProtectedRoute>
        } 
      /> */}
      <Route 
        path="/project-alerts" 
        element={
          <ProtectedRoute>
            <ProjectAlerts />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/notification-settings" 
        element={
          <ProtectedRoute>
            <NotificationSettings />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/sprint-settings" 
        element={
          <ProtectedRoute>
            <SprintSettings />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/notifications" 
        element={
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        } 
      />
      <Route path="/strategist-model-settings" element={<ProtectedRoute><StrategistModelSettings /></ProtectedRoute>} />
      <Route 
        path="/strategist-agent" 
        element={
          <ProtectedRoute>
            <StrategistAgentDashboard />
          </ProtectedRoute>
        } 
      />

      <Route path="/test-bg" element={<LoginBackground />} />

      <Route path="/" element={<Navigate to="/login" />} />
    </Routes>
  );
};

export default AppRoutes;
